import React, { useEffect, useMemo, useState } from "react";
import { fetchDailyReport, fetchSummaryReport, fetchMatters, fetchTags, fetchUsers, fetchUsersLite, fetchDailyByUser } from "../lib/api";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import LodestarTab from "../components/LodestarTab";
import { todayISO, ndaysAgoISO } from "../lib/dates";

const LS_KEY = 'hourslaw.reports.filters';
const LS_FILE_KEY = 'hourslaw.reports.filenameFilters';

export default function ReportsPage(){
  const { user } = useAuth();
  const role = user?.role || 'user';
  const isAdmin = role === 'admin' || role === 'superadmin';
  const isSuper = role === 'superadmin';

  const [activeTab, setActiveTab] = useState("reports");
  const [range, setRange] = useState("14");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");


  const [matters, setMatters] = useState([]);
  const [tags, setTags] = useState([]);


  const [projectId, setProjectId] = useState("");
  const [tagId, setTagId] = useState("");

  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [userSearch, setUserSearch] = useState("");

  const [rows, setRows] = useState([]); // daily totals
  const [userRows, setUserRows] = useState([]); // summary by user
  const [dailyByUser, setDailyByUser] = useState([]); // per-day, per-user totals

  const [includeFiltersInFilename, setIncludeFiltersInFilename] = useState(false);

  // load filters from localStorage
  useEffect(() => {
    try{
      const raw = localStorage.getItem(LS_KEY);
      if (raw){
        const saved = JSON.parse(raw);
        setRange(saved.range || "14");
        setFromDate(saved.fromDate || "");
        setToDate(saved.toDate || "");

        setProjectId(saved.projectId || "");
        setTagId(saved.tagId || "");
        setSelectedUsers(Array.isArray(saved.selectedUsers) ? saved.selectedUsers : []);
      }
    }catch(e){ console.error('Failed to restore reports filters', e); }
    try{
      const b = localStorage.getItem(LS_FILE_KEY);
      if (b) setIncludeFiltersInFilename(b === '1');
    }catch(e){ console.error('Failed to restore reports filename pref', e); }
  }, []);

  // persist filters
  useEffect(() => {
    const payload = { range, fromDate, toDate, projectId, tagId, selectedUsers };
    localStorage.setItem(LS_KEY, JSON.stringify(payload));
  }, [range, fromDate, toDate, projectId, tagId, selectedUsers]);

  useEffect(() => {
    try{ localStorage.setItem(LS_FILE_KEY, includeFiltersInFilename ? '1' : '0'); }catch(e){ console.error('Failed to persist filename pref', e); }
  }, [includeFiltersInFilename]);

  useEffect(() => {
    (async () => {
      try {
        const [m, t] = await Promise.all([
          fetchMatters(),
          fetchTags()
        ]);
        setMatters(m); setTags(t);
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!isAdmin) return;
      try {
        const list = isSuper ? await fetchUsers() : await fetchUsersLite();
        setAllUsers(list);
      } catch (e) { console.error(e); }
    })();
  }, [isAdmin, isSuper]);

  function computeRange(){
    if (fromDate && toDate) return { from: fromDate, to: toDate };
    if (range === 'all') return { from: '1970-01-01', to: todayISO() };
    let days = parseInt(range,10);
    if (isNaN(days) || days < 1) days = 14;
    let from = ndaysAgoISO(days-1);
    let to = todayISO();
    return { from, to };
  }

  async function load(){
    const { from, to } = computeRange();
    try{
      const data = await fetchDailyReport({ from, to, users: (isAdmin ? selectedUsers : undefined), projectId: projectId || undefined, tagId: tagId || undefined });
      setRows(data);
      if (isAdmin && (selectedUsers?.length || 0) > 0){
        const sdata = await fetchSummaryReport({ from, to, users: selectedUsers, projectId: projectId || undefined, tagId: tagId || undefined });
        setUserRows(sdata);
      } else if (!isAdmin){
        const sdata = await fetchSummaryReport({ from, to });
        setUserRows(sdata);
      } else {
        setUserRows([]);
      }
      // per-user daily data for stacked bars
      if (isAdmin && (selectedUsers?.length || 0) > 1){
        const udata = await fetchDailyByUser({ from, to, users: selectedUsers, projectId: projectId || undefined, tagId: tagId || undefined });
        setDailyByUser(udata);
      } else {
        setDailyByUser([]);
      }
    }catch(e){ console.error(e); }
  }

  useEffect(() => { load(); }, [range, fromDate, toDate, projectId, tagId, JSON.stringify(selectedUsers)]);

  const maxHours = useMemo(() => Math.max(1, ...rows.map(r => r.decimalHours || 0)), [rows]);

  function currentFiltersMeta(){
    const c = 'All matters';
    const m = matters.find(x=>x.id===projectId)?.name || 'All matters';
    const t = tags.find(x=>x.id===tagId)?.name || 'All task codes';
    const { from, to } = computeRange();
    let u = '—';
    if (isAdmin){
      const names = (selectedUsers||[]).map(id => (allUsers.find(u=>u.id===id)?.name || id));
      u = names.length ? names.join('; ') : 'All (org scoped)';
    } else {
      u = user?.name || 'Me';
    }
    return { c, m, t, from, to, u };
  }

  function slug(s){
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'').slice(0,80);
  }

  function exportCSV(){
    const meta = currentFiltersMeta();
    const headerMeta = [
      `Filters`,
      `Timeframe: ${meta.from} → ${meta.to}`,
      `Matter: ${meta.m}`,
      `Task code: ${meta.t}`,
      `Users: ${meta.u}`
    ].join("\n");

    const header = ["date","user_email","total_hours"]; 
    // For daily aggregates, we don't have per-row user; include current user's email or list label
    const userEmail = (isAdmin ? (selectedUsers.length ? `${selectedUsers.length} users` : 'all') : ( (meta.u && meta.u !== '—') ? meta.u : (user?.email || 'me') ));
    const lines = rows.map(r => `${r.date},${userEmail},${(r.decimalHours||0).toFixed(1)}`);
    const csv = [headerMeta, "", header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    let name = `hourslaw_daily_report.csv`;
    if (includeFiltersInFilename){
      const parts = [
        `${meta.from}_to_${meta.to}`,
        meta.m !== 'All matters' ? `m-${slug(meta.m)}` : null,
        meta.t !== 'All task codes' ? `t-${slug(meta.t)}` : null,
        isAdmin ? `u-${(selectedUsers||[]).length || 'all'}` : 'me'
      ].filter(Boolean);
      name = `hourslaw_daily_report_${parts.join('_')}.csv`;
    }

    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  }

  function exportUsersCSV(){
    const meta = currentFiltersMeta();
    const headerMeta = [
      `Filters`,
      `Timeframe: ${meta.from} → ${meta.to}`,
      `Client: ${meta.c}`,
      `Matter: ${meta.m}`,
      `Task code: ${meta.t}`,
      `Users: ${meta.u}`
    ].join("\n");
    const header = ["user","email","total_hours"]; 
    const lines = userRows.map(r => `${r.name},${r.email},${(r.decimalHours||0).toFixed(1)}`);
    const csv = [headerMeta, "", header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    let name = `hourslaw_user_breakdown.csv`;
    if (includeFiltersInFilename){
      const parts = [
        `${meta.from}_to_${meta.to}`,
        meta.m !== 'All matters' ? `m-${slug(meta.m)}` : null,
        meta.t !== 'All task codes' ? `t-${slug(meta.t)}` : null,
        isAdmin ? `u-${(selectedUsers||[]).length || 'all'}` : 'me'
      ].filter(Boolean);
      name = `hourslaw_user_breakdown_${parts.join('_')}.csv`;
    }

    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  }

  const mattersForClient = useMemo(() => {
    return matters;
  }, [matters]);

  function toggleUser(id){
    setSelectedUsers(prev => {
      // If currently showing all (empty array), start with all users and remove the clicked one
      if (prev.length === 0) {
        return allUsers.filter(u => u.id !== id).map(u => u.id);
      }
      // Otherwise toggle normally
      return prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
    });
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold tracking-tight">Reports</h1>
        {/* Tab Navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab("reports")}
            className={`px-4 py-2 text-sm font-medium rounded-t border-b-2 transition-colors ${
              activeTab === "reports" 
                ? "border-primary text-primary bg-muted" 
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid="reports-tab-btn"
          >
            Reports
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab("lodestar")}
              className={`px-4 py-2 text-sm font-medium rounded-t border-b-2 transition-colors ${
                activeTab === "lodestar" 
                  ? "border-primary text-primary bg-muted" 
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="lodestar-tab-btn"
            >
              Lodestar
            </button>
          )}
        </div>
      </div>

      {/* Lodestar Tab Content */}
      {activeTab === "lodestar" && isAdmin && <LodestarTab />}

      {/* Reports Tab Content */}
      {activeTab === "reports" && (
      <>
      <div className="flex items-center justify-end mb-4 gap-3">
        <label className="flex items-center gap-2 text-xs text-muted-foreground" title="Include current filters in exported filename">
          <input type="checkbox" checked={includeFiltersInFilename} onChange={(e)=>setIncludeFiltersInFilename(e.target.checked)} />
          Include filters in CSV filename
        </label>
        <Button variant="secondary" onClick={exportCSV}>Export daily CSV</Button>
        {((isAdmin && selectedUsers.length>0) || (!isAdmin && userRows.length>0)) && (
          <Button variant="secondary" onClick={exportUsersCSV}>Export user breakdown</Button>
        )}
      </div>

      <div className="rounded border p-3 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Quick range</div>
            <Select value={range} onValueChange={(v)=>{ setRange(v); setFromDate(""); setToDate(""); }}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Range"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Past Year</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">From</div>
            <input type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} className="border rounded px-2 py-1 text-sm"/>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">To</div>
            <input type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} className="border rounded px-2 py-1 text-sm"/>
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1">Matter</div>
            <Select value={projectId || "all"} onValueChange={(v)=> setProjectId(v==='all'? '' : v)}>
              <SelectTrigger className="w-[240px]"><SelectValue placeholder="All matters"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All matters</SelectItem>
                {mattersForClient.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Task code</div>
            <Select value={tagId || "all"} onValueChange={(v)=> setTagId(v==='all'? '' : v)}>
              <SelectTrigger className="w-[200px]"><SelectValue placeholder="All task codes"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All task codes</SelectItem>
                {tags.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isAdmin && (
            <div className="ml-auto">
              <Button variant="secondary" onClick={()=>setShowUserPicker(s=>!s)}>Select users</Button>
            </div>
          )}
        </div>
        {isAdmin && showUserPicker && (
          <div className="mt-3 border-t pt-3">
            <div className="mb-2 flex items-center gap-3">
              <input value={userSearch} onChange={(e)=>setUserSearch(e.target.value)} placeholder="Search users..." className="border rounded px-2 py-1 text-sm w-full md:w-80"/>
              <Button 
                size="sm" 
                variant={selectedUsers.length === 0 || selectedUsers.length === allUsers.length ? "default" : "outline"}
                onClick={() => {
                  // Toggle: if all selected or none selected, clear selection (select none)
                  // Otherwise, select all
                  if (selectedUsers.length === 0 || selectedUsers.length === allUsers.length) {
                    // Currently showing all, switch to none
                    setSelectedUsers([]);
                  } else {
                    // Some selected, switch to all
                    setSelectedUsers(allUsers.map(u => u.id));
                  }
                }}
              >
                All
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-56 overflow-auto">
              {allUsers.filter(u => (u.name||'').toLowerCase().includes(userSearch.toLowerCase()) || (u.email||'').toLowerCase().includes(userSearch.toLowerCase())).map(u => (
                <label key={u.id} className="text-sm flex items-center gap-2">
                  <input type="checkbox" checked={selectedUsers.length === 0 || selectedUsers.includes(u.id)} onChange={()=>toggleUser(u.id)} />
                  <span>{u.name} <span className="text-muted-foreground">({u.email})</span></span>
                </label>
              ))}
            </div>
            <div className="mt-2 flex justify-end">
              <Button size="sm" onClick={()=>{ setShowUserPicker(false); load(); }}>Apply</Button>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3 mb-8">
        <div className="text-sm font-medium mb-1">Daily totals</div>
        {rows.length === 0 && (
          <div className="text-sm text-muted-foreground">No data for the selected filters.</div>
        )}
        {/* Stacked daily bars by user */}
        {isAdmin && selectedUsers.length > 1 && (
          <StackedDaily rows={rows} dailyByUser={dailyByUser} users={selectedUsers} allUsers={allUsers} />
        )}
        {/* Fallback single series bars */}
        {(!isAdmin || selectedUsers.length <= 1) && rows.map(r => (
          <div key={r.date} className="flex items-center gap-4">
            <div className="w-28 tabular-nums text-sm">{r.date}</div>
            <div className="flex-1 bg-secondary/60 rounded h-4 overflow-hidden">
              <div
                className="h-full bg-primary/80"
                style={{ width: `${Math.min(100, (r.decimalHours / Math.max(1, ...rows.map(x=>x.decimalHours||0))) * 100)}%` }}
              />
            </div>
            <div className="w-16 text-right tabular-nums text-sm">{(r.decimalHours || 0).toFixed(1)}h</div>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="text-sm font-medium">User breakdown</div>
        {userRows.length === 0 ? (
          <div className="text-sm text-muted-foreground">No user-level data for the selected filters.</div>
        ) : (
          <div className="rounded border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr>
                  <th className="text-left p-2">User</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-right p-2">Total hours</th>
                </tr>
              </thead>
              <tbody>
                {userRows.map(u => (
                  <tr key={u.userId} className="border-t">
                    <td className="p-2">{u.name}</td>
                    <td className="p-2 text-muted-foreground">{u.email}</td>
                    <td className="p-2 text-right tabular-nums">{(u.decimalHours||0).toFixed(1)}h</td>
                  </tr>
                ))}
                {/* Total row - only show if there are users */}
                {userRows.length > 0 && (
                  <tr className="border-t-2 border-primary/20 bg-muted font-bold">
                    <td className="p-3" colSpan={2}>Total</td>
                    <td className="p-3 text-right tabular-nums">
                      {userRows.reduce((sum, u) => sum + (u.decimalHours || 0), 0).toFixed(1)}h
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

function StackedDaily({ rows, dailyByUser, users, allUsers }){
  const max = Math.max(1, ...rows.map(r => r.decimalHours || 0));
  // build a dict keyed by `${date}|${userId}`
  const map = new Map();
  for (const rec of dailyByUser || []){
    map.set(`${rec.date}|${rec.userId}`, rec.decimalHours || (rec.totalTenths? rec.totalTenths/10 : 0));
  }
  const palette = ['#1f77b4','#ff7f0e','#2ca02c','#d62728','#9467bd','#8c564b','#e377c2','#7f7f7f'];
  const colorOf = (uid, idx) => palette[idx % palette.length];

  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const total = r.decimalHours || 0;
        const widthPct = Math.min(100, (total / max) * 100);
        const shares = users.map((uid) => map.get(`${r.date}|${uid}`) || 0);
        const shareTotal = shares.reduce((a,b)=>a+b,0) || 1;
        return (
          <div key={r.date} className="flex items-center gap-4">
            <div className="w-28 tabular-nums text-sm">{r.date}</div>
            <div className="flex-1 bg-secondary/60 rounded h-4 overflow-hidden">
              <div className="h-full flex" style={{ width: `${widthPct}%`, backgroundColor: 'transparent' }}>
                {shares.map((val, idx) => (
                  <div key={users[idx]} title={(allUsers.find(u=>u.id===users[idx])?.name)||users[idx]}
                    className="h-full" style={{ width: `${(val / shareTotal) * 100}%`, backgroundColor: colorOf(users[idx], idx), opacity: 0.9 }} />
                ))}
              </div>
            </div>
            <div className="w-16 text-right tabular-nums text-sm">{total.toFixed(1)}h</div>
          </div>
        );
      })}
    </div>
  );
}